// NOTE: ChMSmobile/src/monitoring/scrub.js is a copy of this file; keep both in step.
//
// Removes personal data (Kenyan phone numbers and email addresses) from an error
// report BEFORE it leaves the device, so Sentry never sees members' contact details.

const REDACTED = '[redacted]';

// Kenyan phone: 07xxxxxxxx, 01xxxxxxxx, 2547xxxxxxxx, +2541xxxxxxxx ...
// The lookarounds stop us matching in the middle of a longer number.
const PHONE = /(?<!\d)(?:\+?254|0)[17]\d{8}(?!\d)/g;
// A simple, forgiving email pattern.
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

// Clean one piece of text. Anything that is not text is returned unchanged.
function scrubString(s) {
  return typeof s === 'string' ? s.replace(EMAIL, REDACTED).replace(PHONE, REDACTED) : s;
}

// Cut a web address off at the first "?" or "#". Everything after those marks
// (e.g. "/register?name=Jane%20Wanjiru&phone=...") can hold names, phone numbers
// or ids - often %-encoded so the patterns above cannot spot them - so we drop it
// all and keep only the page path. The patterns still run afterwards as backup.
function stripQuery(u) {
  if (typeof u !== 'string') return u;
  const cut = u.search(/[?#]/);
  return scrubString(cut === -1 ? u : u.slice(0, cut));
}

// Breadcrumb `data` holds addresses in url / from / to: cut those, then clean the rest.
function scrubData(d) {
  const out = scrubDeep(d);
  if (out && typeof out === 'object' && !Array.isArray(out)) {
    ['url', 'from', 'to'].forEach((k) => {
      if (typeof d[k] === 'string') out[k] = stripQuery(d[k]);
    });
  }
  return out;
}

// Clean text anywhere inside an object/array (used for the free-form `extra` data).
function scrubDeep(v) {
  if (typeof v === 'string') return scrubString(v);
  if (Array.isArray(v)) return v.map(scrubDeep);
  if (v && typeof v === 'object') {
    const out = {};
    Object.keys(v).forEach((k) => { out[k] = scrubDeep(v[k]); });
    return out;
  }
  return v;
}

// Takes a Sentry event (or breadcrumb) and returns a cleaned copy.
export function scrubEvent(event) {
  if (!event || typeof event !== 'object') return event;
  const e = { ...event };

  if ('message' in e) e.message = scrubString(e.message);

  if (e.exception && Array.isArray(e.exception.values)) {
    e.exception = {
      ...e.exception,
      values: e.exception.values.map((x) => ({ ...x, value: scrubString(x.value) })),
    };
  }

  if (Array.isArray(e.breadcrumbs)) {
    // Navigation/fetch breadcrumbs keep URLs in `data` (url, from, to), so clean that too.
    e.breadcrumbs = e.breadcrumbs.map((b) => {
      const c = { ...b, message: scrubString(b.message) };
      if (b.data !== undefined) c.data = scrubData(b.data);
      return c;
    });
  }

  // A single breadcrumb (as passed to beforeBreadcrumb) has `data` at the top level.
  if (e.data !== undefined) e.data = scrubData(e.data);

  // The page the error happened on: keep the path, drop the "?..." part entirely,
  // and cut the "came from" (Referer) address the same way.
  if (e.request && typeof e.request === 'object') {
    const { query_string, ...req } = e.request; // eslint-disable-line no-unused-vars
    if ('url' in req) req.url = stripQuery(req.url);
    if (req.headers && typeof req.headers === 'object') {
      const h = { ...req.headers };
      Object.keys(h).forEach((k) => {
        if (k.toLowerCase() === 'referer') h[k] = stripQuery(h[k]);
      });
      req.headers = h;
    }
    e.request = req;
  }

  if (e.extra) e.extra = scrubDeep(e.extra);

  // Keep only the anonymous user id; drop anything that identifies the person.
  if (e.user) {
    const { email, phone, ip_address, ...rest } = e.user; // eslint-disable-line no-unused-vars
    e.user = rest;
  }
  return e;
}
